import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { UseFormReturn } from "react-hook-form";
import {
  Card,
  Field,
  Checkbox,
  Button,
  Spinner,
} from "@fluentui/react-components";
import { Search24Regular } from "@fluentui/react-icons";

// Import form field components
import TextInputField from "components/controls/TextInputField";
import HookFormSelectField from "components/controls/HookFormSelectField";
import InputField from "components/controls/InputField";
import HookFormTextInputField from "components/controls/HookFormTextInputField";
import { useSharedStyles } from "../styles/sharedStyles";

// Import postal code and city logic
import {
  countryOptions,
  cityOptions,
  findCityByPostalCode,
  findPostalCodesByCity,
  postalCodeOptions,
} from "../../../constants/codelists";
import { getCitiesByPostalCode } from "../../../constants/getCityByPostalCode";
import api from "service/api";
import { makeStyles, tokens } from "@fluentui/react-components";
import HookFormCheckboxField from "components/controls/HookFormCheckboxField";

const useStyles = makeStyles({
  section: {
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacingVerticalL,
  },
  row: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: tokens.spacingHorizontalL,
  },
  fullWidth: {
    gridColumn: "1 / -1",
  },
  subheading: {
    marginTop: tokens.spacingVerticalXL,
    marginBottom: tokens.spacingVerticalM,
    fontSize: tokens.fontSizeBase400,
    fontWeight: tokens.fontWeightSemibold,
  },
});

type Props = {
  form: UseFormReturn<any, any>;
  isLoading: boolean;
};

const ClientSection = ({ form, isLoading }: Props) => {
  const { t } = useTranslation();
  const classes = useSharedStyles();
  const { control, watch } = form;

  const clientType = form.watch("clientType");
  const addressPostalCode = form.watch("client.address.index");
  const addressCity = form.watch("client.address.city");
  const corrPostalCode = form.watch("client.correspondenceAddress.index");
  const corrCity = form.watch("client.correspondenceAddress.city");
  const [isIcoLookupLoading, setIsIcoLookupLoading] = useState(false);
  const icoValue = form.watch("client.code");

  // Auto-select city when postal code changes for main address
  useEffect(() => {
    if (addressPostalCode && addressPostalCode.length === 5) {
      const cityKey = findCityByPostalCode(addressPostalCode);
      if (cityKey && cityKey !== addressCity) {
        form.setValue("client.address.city", cityKey);
      }
    }
  }, [addressPostalCode, form, addressCity]);

  // Auto-select postal code when city changes for main address
  useEffect(() => {
    if (addressCity) {
      const matchingPostalCodes = findPostalCodesByCity(addressCity);
      if (
        matchingPostalCodes.length === 1 &&
        matchingPostalCodes[0] !== addressPostalCode
      ) {
        form.setValue("client.address.index", matchingPostalCodes[0]);
      } else if (matchingPostalCodes.length > 0 && !addressPostalCode) {
        form.setValue("client.address.index", matchingPostalCodes[0]);
      }
    }
  }, [addressCity, form, addressPostalCode]);

  // Auto-select city when postal code changes for correspondence address
  useEffect(() => {
    if (corrPostalCode && corrPostalCode.length === 5) {
      const cityKey = findCityByPostalCode(corrPostalCode);
      if (cityKey && cityKey !== corrCity) {
        form.setValue("client.correspondenceAddress.city", cityKey);
      }
    }
  }, [corrPostalCode, form, corrCity]);

  // Auto-select postal code when city changes for correspondence address
  useEffect(() => {
    if (corrCity) {
      const matchingPostalCodes = findPostalCodesByCity(corrCity);
      if (
        matchingPostalCodes.length === 1 &&
        matchingPostalCodes[0] !== corrPostalCode
      ) {
        form.setValue(
          "client.correspondenceAddress.index",
          matchingPostalCodes[0]
        );
      } else if (matchingPostalCodes.length > 0 && !corrPostalCode) {
        form.setValue(
          "client.correspondenceAddress.index",
          matchingPostalCodes[0]
        );
      }
    }
  }, [corrCity, form, corrPostalCode]);

  // ICO lookup function
  const handleIcoLookup = async () => {
    if (!icoValue || icoValue.trim().length === 0) {
      return;
    }

    setIsIcoLookupLoading(true);
    try {
      // Call the ICO lookup API
      const response = await api.getClientInfoByIco(icoValue.trim());

      if (response.status === 200 && response.data) {
        const clientData = response.data;
        if (clientData) {
          if (clientData.name) {
            form.setValue("client.name", clientData.name);
          }
          if (clientData.region) {
            form.setValue("client.region", clientData.region);
          }
          if (clientData.regionCode) {
            form.setValue("client.regionCode", clientData.regionCode);
          }
          if (clientData.address) {
            const address = clientData.address;
            if (address.country) {
              form.setValue("client.address.country", address.country);
            }
            if (address.city) {
              form.setValue("client.address.city", address.city);
            }
            if (address.street) {
              form.setValue("client.address.street", address.street);
            }
            if (address.house) {
              form.setValue("client.address.house", address.house);
            }
            if (address.flat) {
              form.setValue("client.address.flat", address.flat);
            }
            if (address.index) {
              form.setValue("client.address.index", address.index);
            }
            if (address.addressString) {
              form.setValue(
                "client.address.addressString",
                address.addressString
              );
            }
          }
          if (clientData.isCompany !== undefined) {
            form.setValue("client.isCompany", clientData.isCompany);
          } else if (clientData.company !== undefined) {
            form.setValue("client.isCompany", clientData.company);
          }
        }
      }
    } catch (error: any) {
      console.error("ICO lookup error:", error);
    } finally {
      setIsIcoLookupLoading(false);
    }
  };

  // Update isCompany field and reset form values when clientType changes
  useEffect(() => {
    if (clientType) {
      const isCompany =
        clientType === "pravnicka_osoba" || clientType === "osvc";
      form.setValue("client.isCompany", isCompany);

      // Reset fields based on client type to prevent data mixing
      if (clientType === "obcan") {
        form.setValue("client.code", "");
        form.setValue("client.name", "");
      } else if (clientType === "pravnicka_osoba" || clientType === "osvc") {
        form.setValue("client.firstName", "");
        form.setValue("client.lastName", "");
      }
    }
  }, [clientType, form]);

  // Watch for isCompany to conditionally render fields
  const isCompany = watch("client.isCompany");

  return (
    <Card className={classes.card}>
      <div className={classes.section}>
        <div className={classes.grid}>
          <div className={classes.sectionTitle}>{t("ila.client.title")}</div>
          <HookFormSelectField
            label={t("ila.client.clientType")}
            name="clientType"
            form={form}
            options={[
              { value: "obcan", label: t("ila.client.individual") },
              { value: "osvc", label: t("ila.client.soleTrader") },
              { value: "pravnicka_osoba", label: t("ila.client.legalEntity") },
            ]}
            required={true}
            disabled={isLoading}
          />
          {/* Company fields */}
          {(clientType === "pravnicka_osoba" || clientType === "osvc") && (
            <>
              <InputField
                label={t("ila.client.code")}
                name="client.code"
                required={false}
                disabled={isLoading}
                value={form.watch("client.code") || ""}
                setFieldValue={(name, value) =>
                  form.setValue(name, value, { shouldValidate: true })
                }
                error={
                  (form.formState.errors?.client?.code?.message as string) || ""
                }
                contentAfter={
                  <>
                    {isIcoLookupLoading && <Spinner size="tiny" />}
                    <Button
                      appearance="subtle"
                      icon={<Search24Regular />}
                      title={t("ila.client.icoLookupTooltip")}
                      aria-label={t("ila.client.icoLookupTooltip")}
                      onClick={handleIcoLookup}
                      disabled={
                        isLoading || isIcoLookupLoading || !icoValue?.trim()
                      }
                      size="small"
                    />
                  </>
                }
              />
              <HookFormTextInputField
                label={t("ila.client.name")}
                name="client.name"
                required={true}
                disabled={isLoading}
                control={form.control}
              />
            </>
          )}

          {/* Individual fields */}
          {(clientType === "obcan" || clientType === "osvc") && (
            <>
              <HookFormTextInputField
                label={t("ila.client.firstName")}
                name="client.firstName"
                required={true}
                disabled={isLoading}
                control={form.control}
              />
              <HookFormTextInputField
                label={t("ila.client.lastName")}
                name="client.lastName"
                required={true}
                disabled={isLoading}
                control={form.control}
              />
              <HookFormTextInputField
                label={t("ila.client.birthNumber")}
                name="client.birthNumber"
                required={true}
                disabled={isLoading}
                control={form.control}
              />
            </>
          )}
          {(clientType === "pravnicka_osoba" || clientType === "osvc") && (
            <HookFormCheckboxField
              label={t("ila.client.vatPayer")}
              name="client.vatPayer"
              form={form}
              disabled={isLoading}
            />
          )}

          {/* Common fields for all client types */}
          {/* Removed unnecessary fields: region, regionCode, invalidityCode, isResident */}
          {/* Address fields */}
          {clientType && (
            <>
              <div className={classes.sectionTitle}>
                {t("ila.client.address")}
              </div>
              {/* Removed addressString and village */}
              <HookFormTextInputField
                label={t("ila.client.address.street")}
                name="client.address.street"
                required={true}
                disabled={isLoading}
                control={form.control}
              />
              <HookFormTextInputField
                label={t("ila.client.address.house")}
                name="client.address.house"
                required={true}
                disabled={isLoading}
                control={form.control}
              />
              <HookFormTextInputField
                label={t("ila.client.address.flat")}
                name="client.address.flat"
                required={false}
                disabled={isLoading}
                control={form.control}
              />
              <HookFormSelectField
                label={t("ila.client.address.index")}
                name="client.address.index"
                form={form}
                options={postalCodeOptions.map((option) => ({
                  value: option.key,
                  label: option.text,
                }))}
                required={true}
                disabled={isLoading}
              />
              <HookFormSelectField
                label={t("ila.client.address.city")}
                name="client.address.city"
                form={form}
                options={(() => {
                  const currentPostalCode = addressPostalCode;
                  const availableCities = currentPostalCode
                    ? getCitiesByPostalCode(currentPostalCode)
                    : cityOptions;
                  return availableCities.map((option) => ({
                    value: option.key,
                    label: option.text,
                  }));
                })()}
                required={true}
                disabled={isLoading}
              />
              {/* <HookFormSelectField
                label={t("ila.client.address.country")}
                name="client.address.country"
                form={form}
                options={countryOptions.map((option) => ({
                  value: option.key,
                  label: option.text,
                }))}
                required={false}
                disabled={isLoading}
              /> */}
              <HookFormTextInputField
                label={t("ila.client.address.suburb")}
                name="client.address.suburb"
                required={false}
                disabled={isLoading}
                control={form.control}
              />
              <div className={classes.sectionTitle}>
                {t("ila.client.contact")}
              </div>
              <HookFormTextInputField
                label={t("ila.client.address.phone")}
                name="client.address.phone"
                required={false}
                disabled={isLoading}
                control={form.control}
              />
              <HookFormTextInputField
                label={t("ila.client.address.email")}
                name="client.address.email"
                required={false}
                disabled={isLoading}
                control={form.control}
              />
              <HookFormTextInputField
                label={t("ila.client.address.mobilePhone")}
                name="client.address.mobilePhone"
                required={false}
                disabled={isLoading}
                control={form.control}
              />
            </>
          )}
          {/* Correspondence Address checkbox and fields */}
          {clientType && (
            <>
              <Field
                label={t("ila.client.correspondenceAddressDifferent")}
                className={classes.sectionTitle}
              >
                <Checkbox
                  checked={
                    form.watch("correspondenceAddressDifferent") || false
                  }
                  onChange={(_ev, data) =>
                    form.setValue(
                      "correspondenceAddressDifferent",
                      data.checked
                    )
                  }
                  disabled={isLoading}
                />
              </Field>
            </>
          )}
          {/* Correspondence Address fields - only show if checkbox is checked */}
          {clientType && form.watch("correspondenceAddressDifferent") && (
            <>
              <div className={classes.sectionTitle}>
                {t("ila.client.correspondenceAddress")}
              </div>
              {/* Removed addressString and village */}
              <HookFormTextInputField
                label={t("ila.client.correspondenceAddress.street")}
                name="client.correspondenceAddress.street"
                required={true}
                disabled={isLoading}
                control={form.control}
              />
              <HookFormTextInputField
                label={t("ila.client.correspondenceAddress.house")}
                name="client.correspondenceAddress.house"
                required={true}
                disabled={isLoading}
                control={form.control}
              />
              <HookFormTextInputField
                label={t("ila.client.correspondenceAddress.flat")}
                name="client.correspondenceAddress.flat"
                required={false}
                disabled={isLoading}
                control={form.control}
              />
              <HookFormSelectField
                label={t("ila.client.correspondenceAddress.index")}
                name="client.correspondenceAddress.index"
                form={form}
                options={postalCodeOptions.map((option) => ({
                  value: option.key,
                  label: option.text,
                }))}
                required={true}
                disabled={isLoading}
              />
              <HookFormSelectField
                label={t("ila.client.correspondenceAddress.city")}
                name="client.correspondenceAddress.city"
                options={(() => {
                  const currentPostalCode = corrPostalCode;
                  const availableCities = currentPostalCode
                    ? getCitiesByPostalCode(currentPostalCode)
                    : cityOptions;
                  return availableCities.map((option) => ({
                    value: option.key,
                    label: option.text,
                  }));
                })()}
                form={form}
                required={true}
                disabled={isLoading}
              />
              {/* <HookFormSelectField
                label={t("ila.client.correspondenceAddress.country")}
                name="client.correspondenceAddress.country"
                form={form}
                options={countryOptions.map((option) => ({
                  value: option.key,
                  label: option.text,
                }))}
                required={false}
                disabled={isLoading}
              /> */}
              <HookFormTextInputField
                label={t("ila.client.correspondenceAddress.suburb")}
                name="client.correspondenceAddress.suburb"
                required={false}
                disabled={isLoading}
                control={form.control}
              />
            </>
          )}
          {/* Electronic Communication and Marketing */}
          {clientType && (
            <>
              <div className={classes.sectionTitle}>
                {t("ila.communicationConsent")}
              </div>
              <HookFormSelectField
                label={t("ila.elkom")}
                name="elkom"
                form={form}
                options={[
                  { value: "A", label: t("ila.elkomYes") },
                  { value: "N", label: t("ila.elkomNo") },
                ]}
                required={true}
                disabled={isLoading}
              />
              <HookFormSelectField
                label={t("ila.marketing")}
                name="marketing"
                form={form}
                options={[
                  { value: "V", label: t("ila.marketingV") },
                  { value: "A", label: t("ila.marketingA") },
                  { value: "N", label: t("ila.marketingN") },
                ]}
                required={true}
                disabled={isLoading}
              />
            </>
          )}
        </div>
      </div>
    </Card>
  );
};

export default ClientSection;
